import api from '../lib/api';
import type {
  XServicesService,
  SubscriptionPlan,
  ProfileSubscription,
  SubscriptionRequest,
  SocietySubscriptionStatus,
  EligibilityResult,
  RoleChangeRequest,
} from '../types';
import type { ApiResponse } from '../types';

const BASE = '/subscriptions';
const ROLES_BASE = '/roles';

export const subscriptionApi = {

  getServices: async (): Promise<XServicesService[]> => {
    const res = await api.get<ApiResponse<XServicesService[]>>(`${BASE}/services`);
    return res.data.data;
  },

  getPlansByService: async (serviceId: number): Promise<SubscriptionPlan[]> => {
    const res = await api.get<ApiResponse<SubscriptionPlan[]>>(`${BASE}/services/${serviceId}/plans`);
    return res.data.data;
  },

  getSocietyActiveStatus: async (societyId: number): Promise<SocietySubscriptionStatus> => {
    const res = await api.get<ApiResponse<SocietySubscriptionStatus>>(`${BASE}/society/${societyId}/active`);
    return res.data.data;
  },

  subscribe: async (payload: SubscriptionRequest): Promise<ProfileSubscription> => {
    const res = await api.post<ApiResponse<ProfileSubscription>>(BASE, payload);
    return res.data.data;
  },

  getProfileSubscriptions: async (profileId: number): Promise<ProfileSubscription[]> => {
    const res = await api.get<ApiResponse<ProfileSubscription[]>>(`${BASE}/profile/${profileId}`);
    return res.data.data;
  },

  getActiveSubscription: async (profileId: number, serviceId: number): Promise<ProfileSubscription | null> => {
    const res = await api.get<ApiResponse<ProfileSubscription | null>>(
      `${BASE}/profile/${profileId}/service/${serviceId}/active`
    );
    return res.data.data ?? null;
  },

  renew: async (subscriptionId: number, planId: number): Promise<ProfileSubscription> => {
    const res = await api.put<ApiResponse<ProfileSubscription>>(
      `${BASE}/${subscriptionId}/renew?planId=${planId}`
    );
    return res.data.data;
  },

  cancel: async (subscriptionId: number): Promise<void> => {
    await api.delete(`${BASE}/${subscriptionId}`);
  },

  getReactivatableSubscription: async (profileId: number, serviceId: number): Promise<ProfileSubscription | null> => {
    const res = await api.get<ApiResponse<ProfileSubscription | null>>(
      `${BASE}/profile/${profileId}/service/${serviceId}/reactivatable`
    );
    return res.data.data ?? null;
  },

  resume: async (subscriptionId: number, profileId: number): Promise<ProfileSubscription> => {
    const res = await api.post<ApiResponse<ProfileSubscription>>(
      `${BASE}/${subscriptionId}/resume?profileId=${profileId}`
    );
    return res.data.data;
  },

  // ─── Eligibility ──────────────────────────────────────────────────────────

  checkSocietyEligibility: async (profileId: number): Promise<EligibilityResult> => {
    const res = await api.get<ApiResponse<EligibilityResult>>(
      `${ROLES_BASE}/subscription/eligibility/society?profileId=${profileId}`
    );
    return res.data.data;
  },

  // ─── Role Change Requests ─────────────────────────────────────────────────

  submitRoleChangeRequest: async (payload: {
    profileId: number;
    serviceId: number;
    contextEntityId?: number;
    currentRoleTypeId: number;
    requestedRoleTypeId: number;
    reason: string;
  }): Promise<RoleChangeRequest> => {
    const res = await api.post<ApiResponse<RoleChangeRequest>>(`${ROLES_BASE}/request`, payload);
    return res.data.data;
  },

  getPendingRoleRequests: async (contextEntityId: number, serviceId: number): Promise<RoleChangeRequest[]> => {
    const res = await api.get<ApiResponse<RoleChangeRequest[]>>(
      `${ROLES_BASE}/requests/pending?contextEntityId=${contextEntityId}&serviceId=${serviceId}`
    );
    return res.data.data;
  },

  getMyRoleRequests: async (profileId: number): Promise<RoleChangeRequest[]> => {
    const res = await api.get<ApiResponse<RoleChangeRequest[]>>(
      `${ROLES_BASE}/requests/my?profileId=${profileId}`
    );
    return res.data.data;
  },

  approveRoleRequest: async (requestId: number, reviewerProfileId: number, remarks?: string): Promise<void> => {
    await api.put(`${ROLES_BASE}/requests/${requestId}/approve`, { reviewerProfileId, remarks });
  },

  rejectRoleRequest: async (requestId: number, reviewerProfileId: number, remarks?: string): Promise<void> => {
    await api.put(`${ROLES_BASE}/requests/${requestId}/reject`, { reviewerProfileId, remarks });
  },
};
